import React, { useState, useEffect } from 'react';
import { API } from '../../App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { IndianRupee, TrendingDown, Wallet, AlertCircle, Users, Receipt } from 'lucide-react';
import { toast } from 'sonner';

const KpiCard = ({ icon: Icon, label, value, color }) => (
  <Card className="border-0 shadow-md">
    <CardContent className="p-5 flex items-center justify-between">
      <div>
        <p className="text-xs font-medium text-slate-500">{label}</p>
        <p className={`text-2xl font-bold ${color}`}>{value}</p>
      </div>
      <div className={`w-10 h-10 rounded-lg ${color.replace('text', 'bg').replace('600', '100')} flex items-center justify-center`}>
        <Icon className={`w-5 h-5 ${color}`} />
      </div>
    </CardContent>
  </Card>
);

const FinanceReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/finance/report`)
      .then((r) => setData(r.data))
      .catch(() => toast.error('Failed to load finance report'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center py-20 text-slate-600">Loading finance report...</div>;
  if (!data) return null;

  const s = data.summary;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="finance-report">
      <div className="mb-6">
        <h1 className="text-4xl font-bold text-slate-900 mb-2">Financial Reports</h1>
        <p className="text-lg text-slate-600">Revenue, payouts and salary overview</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        <KpiCard icon={IndianRupee} label="Total Revenue" value={`₹${s.total_revenue}`} color="text-emerald-600" />
        <KpiCard icon={Wallet} label="Teacher Payouts" value={`₹${s.total_payout}`} color="text-indigo-600" />
        <KpiCard icon={Users} label="Salaries Paid" value={`₹${s.salaries_paid}`} color="text-sky-600" />
        <KpiCard icon={Receipt} label="Other Expenses" value={`₹${s.expenses_paid}`} color="text-rose-600" />
        <KpiCard icon={TrendingDown} label="Net" value={`₹${s.net}`} color="text-violet-600" />
        <KpiCard icon={AlertCircle} label="Pending In/Out"
          value={`₹${s.pending_amount} / ₹${(s.salaries_pending || 0) + (s.expenses_pending || 0)}`}
          color="text-amber-600" />
      </div>

      <Card className="border-0 shadow-lg mb-6">
        <CardHeader><CardTitle>Revenue vs Payout vs Expenses (6 months)</CardTitle></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.monthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis dataKey="month" fontSize={12} />
              <YAxis fontSize={12} />
              <Tooltip />
              <Legend />
              <Bar dataKey="revenue" fill="#10b981" name="Revenue (₹)" radius={[6, 6, 0, 0]} />
              <Bar dataKey="payout" fill="#6366f1" name="Payout (₹)" radius={[6, 6, 0, 0]} />
              <Bar dataKey="expenses" fill="#f43f5e" name="Expenses (₹)" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-0 shadow-lg">
          <CardHeader><CardTitle>Salary / Teacher Payouts</CardTitle></CardHeader>
          <CardContent>
            {data.teacher_salaries.length === 0 ? (
              <p className="text-center text-slate-500 py-6">No payout data.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="salary-table">
                  <thead>
                    <tr className="text-left text-slate-500 border-b">
                      <th className="py-2 pr-4">Teacher</th>
                      <th className="py-2 px-2">Rate/hr</th>
                      <th className="py-2 px-2">Sessions</th>
                      <th className="py-2 px-2">Hours</th>
                      <th className="py-2 px-2">Payout</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.teacher_salaries.map((t) => (
                      <tr key={t.teacher_id} className="border-b border-slate-50 hover:bg-slate-50">
                        <td className="py-2 pr-4 font-medium text-slate-800">{t.teacher_name}</td>
                        <td className="py-2 px-2">₹{t.rate_per_hour}</td>
                        <td className="py-2 px-2">{t.sessions}</td>
                        <td className="py-2 px-2">{t.hours}</td>
                        <td className="py-2 px-2 font-semibold text-emerald-600">₹{t.payout}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg">
          <CardHeader><CardTitle>Recent Payments · Collection by Method</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2 mb-4">
              {Object.entries(data.by_method).map(([m, v]) => (
                <Badge key={m} variant="secondary" className="bg-emerald-50 text-emerald-700">{m}: ₹{v}</Badge>
              ))}
            </div>
            <div className="divide-y divide-slate-100">
              {data.recent_payments.length === 0 ? (
                <p className="text-center text-slate-500 py-6">No payments yet.</p>
              ) : (
                data.recent_payments.map((p) => (
                  <div key={p.id} className="flex items-center justify-between py-2" data-testid={`finance-payment-${p.id}`}>
                    <div>
                      <div className="font-medium text-slate-800">{p.student_name}</div>
                      <div className="text-xs text-slate-500">{p.plan || 'Payment'} • {p.method} • {p.paid_on}</div>
                    </div>
                    <div className="font-semibold text-emerald-600">₹{p.amount}</div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default FinanceReport;
